import React, { useEffect, useRef, useState } from 'react';
import { IconButton, InputAdornment, SvgIcon, TextField } from '@mui/material';
import { completerFrappe, isoVersTexte, texteVersIso } from './dates';

// Le champ date du navigateur ne se tape pas partout : sur téléphone il
// n'ouvre que le calendrier, sur ordinateur il impose sa saisie case par case.
// Ici on tape « 09/10/2026 » librement, et le calendrier reste à un clic.

const IconeCalendrier = (props) => (
  <SvgIcon {...props}>
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM7 11h5v5H7z" />
  </SvgIcon>
);

// Les formulaires lisent e.target.value : on leur rend la même forme.
const evenement = (value) => ({ target: { value, type: 'date' } });

/**
 * Remplace <TextField type="date">. `value` et la valeur rendue par
 * `onChange` restent au format AAAA-MM-JJ, '' quand le champ est vide.
 */
export default function ChampDate({
  value, onChange, disabled, helperText, InputLabelProps, ...reste
}) {
  const [texte, setTexte] = useState(() => isoVersTexte(value));
  const [invalide, setInvalide] = useState(false);
  const calendrier = useRef(null);

  // Une valeur posée de l'extérieur (chargement, calendrier) remplace la
  // saisie — sauf si c'est déjà ce que la saisie dit.
  useEffect(() => {
    const iso = (value || '').slice(0, 10);
    if (iso !== (texteVersIso(texte) || '')) {
      setTexte(isoVersTexte(iso));
      setInvalide(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const taper = (e) => {
    const nouveau = completerFrappe(texte, e.target.value);
    setTexte(nouveau);
    setInvalide(false);
    if (!nouveau.trim()) {
      onChange?.(evenement(''));
      return;
    }
    const iso = texteVersIso(nouveau);
    // Une date à moitié tapée ne remonte pas : le formulaire garde la
    // dernière date complète.
    if (iso) onChange?.(evenement(iso));
  };

  const quitter = () => {
    if (texte.trim() && !texteVersIso(texte)) setInvalide(true);
  };

  const ouvrirCalendrier = () => {
    const champ = calendrier.current;
    if (!champ) return;
    try {
      champ.showPicker();
    } catch {
      champ.focus();
      champ.click();
    }
  };

  const choisir = (e) => {
    setTexte(isoVersTexte(e.target.value));
    setInvalide(false);
    onChange?.(evenement(e.target.value));
  };

  return (
    <TextField
      {...reste}
      value={texte}
      onChange={taper}
      onBlur={quitter}
      disabled={disabled}
      error={invalide || reste.error}
      helperText={invalide ? 'Date attendue : jj/mm/aaaa' : helperText}
      placeholder="jj/mm/aaaa"
      InputLabelProps={{ shrink: true, ...InputLabelProps }}
      inputProps={{ inputMode: 'numeric', autoComplete: 'off',
                    maxLength: 10, ...reste.inputProps }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" sx={{ position: 'relative' }}>
            <IconButton size="small" edge="end" onClick={ouvrirCalendrier}
                        disabled={disabled} aria-label="Ouvrir le calendrier">
              <IconeCalendrier fontSize="small" />
            </IconButton>
            {/* Le calendrier du navigateur, invisible : on n'en garde que la fenêtre. */}
            <input ref={calendrier} type="date" tabIndex={-1} aria-hidden
                   value={(value || '').slice(0, 10)} onChange={choisir}
                   style={{ position: 'absolute', right: 0, bottom: 0,
                            width: 1, height: 1, opacity: 0, border: 0,
                            padding: 0, pointerEvents: 'none' }} />
          </InputAdornment>
        ),
        ...reste.InputProps,
      }}
    />
  );
}
